'use client';
import { useTranslations, useLocale } from 'next-intl';
import styled from 'styled-components';

import ScaleValue from '@/app/[locale]/(full-width)/filament-material-guide/MaterialsTable/cells/ScaleValue';
import Button from '@/ui/components/Button';
import Flags from '@/ui/components/Flags';
import Image from '@/ui/components/Image';
import Link from '@/ui/components/Link';
import { useManual } from '@/ui/components/Manual/ManualContextProvider';
import { calculateAverageDifficulty } from '@/utils/assemblyManuals/difficulty';
import { urlBuilder } from '@/utils/urlBuilder';
import { addLocalePath } from '@/utils/urlBuilder/utils';

type IsTabOpenProps = {
  $isTabOpen: boolean;
};

export const Wrapper = styled.div<IsTabOpenProps>`
  align-items: center;
  align-self: stretch;
  display: flex;
  flex-wrap: ${props => (props.$isTabOpen ? 'wrap' : 'nowrap')};
  gap: 20px;
  margin-top: 10px;

  ${({ theme }) => theme.breakpoints.down('md')} {
    flex-wrap: wrap;
  }
`;

export const ProductHeader = styled.div`
  text-align: center;
  width: 200px;

  ${({ theme }) => theme.breakpoints.down('md')} {
    width: 100%;
  }

  img,
  .pulse {
    align-self: flex-start;
    height: 200px;
    object-fit: cover;
    mix-blend-mode: multiply;
    width: 200px;
  }
`;

const List = styled.ul`
  border-top: 1px ${props => props.theme.palette.gray.main} solid;
  display: flex;
  flex-flow: column;
  margin: 0;
  padding: 0;
  width: 100%;
`;

const ListItem = styled.li`
  align-items: center;
  border-bottom: 1px ${props => props.theme.palette.gray.main} solid;
  display: flex;
  gap: 20px;
  padding: 15px 20px;
`;

const ListTitle = styled.h2`
  font-size: 14px;
  line-height: 1;
  font-family: monospace;
  font-weight: 400;
  margin: 0;
  width: 165px;
`;

const ListContent = styled.div`
  align-items: center;
  display: flex;
  gap: 10px;
`;

export const Content = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1.25rem;
  flex: 1 0 0;

  ${({ theme }) => theme.breakpoints.down('md')} {
    width: 100%;
  }
`;

const AssemblyButton = styled.div`
  ${({ theme }) => theme.breakpoints.down('md')} {
    width: 100%;

    button {
      width: 100%;
    }
  }
`;

const DownloadButton = styled.div`
  display: flex;
  gap: 0.625rem;

  ${({ theme }) => theme.breakpoints.down('md')} {
    justify-content: space-around;
    width: 100%;
  }
`;

interface HeaderProps {
  category: CategoryData;
  pdf: PdfData;
  guides: GuideData[];
  entryStep: boolean;
  nextStep?: string | null;
}

export default function Header({ category, entryStep, guides, nextStep, pdf }: HeaderProps) {
  const locale = useLocale();
  const t = useTranslations();
  const { sidebar } = useManual();
  const firstGuide = guides[0];

  const difficulties = guides.filter(guide => guide?.meta?.difficulty).map(guide => guide.meta.difficulty);
  const difficulty = calculateAverageDifficulty(difficulties);

  // entry step vs assembly manual
  const source = entryStep ? firstGuide.translations_info : category.translations_status;
  const baseUrl = entryStep ? 'guide' : 'manual';
  const { [locale]: _, ...otherTranslations } = source;

  const availableTranslations = Object.keys(otherTranslations).map(lng => ({
    locale: lng,
    link: addLocalePath(lng, `${baseUrl}/${otherTranslations[lng].slug}`),
  }));

  return (
    <Wrapper $isTabOpen={sidebar !== null}>
      <ProductHeader>
        {guides?.length === 1 ? (
          <Image src={guides?.[0]?.media} alt={guides?.[0]?.title} />
        ) : (
          <Image src={category.media} alt={category.title} />
        )}
      </ProductHeader>
      <Content>
        <List>
          {!!difficulty?.numeric && (
            <ListItem>
              <ListTitle>{t('guideCategory-difficulty')}</ListTitle>
              <ListContent>
                <ScaleValue value={difficulty.numeric} reversed />
                <strong>{t(`guideCategory-difficulty-${difficulty.label.toLowerCase().replace(/\s+/g, '-')}`)}</strong>
              </ListContent>
            </ListItem>
          )}
          <ListItem>
            <ListTitle>{t('guideCategory-available-languages')}</ListTitle>
            <ListContent>
              <Flags items={availableTranslations} />
            </ListContent>
          </ListItem>
        </List>
        <AssemblyButton>
          <Link href={urlBuilder({ item: firstGuide, locale }) + (nextStep ? `#${nextStep}` : '')}>
            <Button>{t('guideCategory-begin-assembly')}</Button>
          </Link>
        </AssemblyButton>
        {pdf && (
          <DownloadButton>
            <a href={pdf.url} target="_blank" rel="noreferrer">
              <Button variant="outlined">{t('guideCategory-download-manual')}</Button>
            </a>
          </DownloadButton>
        )}
      </Content>
    </Wrapper>
  );
}
